import type { ReactNode } from 'react';

import { cn } from '@psspl/utils';

export type InlineAlertTone = 'danger' | 'warning' | 'success' | 'info';

export interface InlineAlertProps {
  readonly tone: InlineAlertTone;
  /** Optional bold first line. */
  readonly title?: string;
  readonly children: ReactNode;
  readonly className?: string | undefined;
}

const TONE_CLASS: Record<InlineAlertTone, { readonly frame: string; readonly icon: string }> = {
  danger: { frame: 'border-feedback-danger/35 bg-feedback-danger/8', icon: 'text-feedback-danger' },
  warning: {
    frame: 'border-feedback-warning/35 bg-feedback-warning/8',
    icon: 'text-feedback-warning',
  },
  success: {
    frame: 'border-feedback-success/35 bg-feedback-success/8',
    icon: 'text-feedback-success',
  },
  info: { frame: 'border-brand-500/30 bg-brand-500/8', icon: 'text-brand-300' },
};

/**
 * Inline feedback message.
 *
 * One component for the error, warning and success notes that were previously
 * hand-styled on each screen. Tone is always paired with an icon and text, never
 * carried by colour alone (`docs/ui-guidelines.md` section 8.3). Danger and warning
 * are announced assertively; success and info politely.
 */
export function InlineAlert({ tone, title, children, className }: InlineAlertProps) {
  const style = TONE_CLASS[tone];
  const isUrgent = tone === 'danger' || tone === 'warning';

  return (
    <div
      role={isUrgent ? 'alert' : 'status'}
      className={cn('flex items-start gap-3 rounded-md border px-3 py-2.5', style.frame, className)}
    >
      <svg
        viewBox="0 0 20 20"
        className={cn('mt-0.5 size-4 shrink-0', style.icon)}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        aria-hidden="true"
      >
        {tone === 'success' ? (
          <path d="m5.5 10.5 3 3 6-7" strokeLinecap="round" strokeLinejoin="round" />
        ) : (
          <>
            <circle cx="10" cy="10" r="7.5" />
            <path
              d={tone === 'info' ? 'M10 9v4.5M10 6.5h.01' : 'M10 6v4.5M10 13.5h.01'}
              strokeLinecap="round"
            />
          </>
        )}
      </svg>
      <div className="flex min-w-0 flex-col gap-0.5 text-sm leading-relaxed">
        {title === undefined ? null : <p className="font-medium text-ink-primary">{title}</p>}
        <div className="text-ink-secondary">{children}</div>
      </div>
    </div>
  );
}
